import { useState, useEffect } from 'react'
import axios from 'axios'
import { authHeaders } from '../CuentaCompensacion'
import styles from './Modal.module.css'

const API = import.meta.env.VITE_API_GATEWAY_URL
const MONEDAS = ['USD', 'EUR', 'GBP', 'COP', 'JPY', 'CNY', 'CHF']

async function uploadDocToS3(file, empresaId) {
    const ext = file.name.split('.').pop().toLowerCase()
    const ct  = ext === 'pdf' ? 'application/pdf' : 'application/octet-stream'
    const headers = await authHeaders()
    const res = await axios.post(`${API}/upload-url`, {
        fileTypes: ['cc_doc'],
        fileExtensions: { cc_doc: ext },
        contentTypes:   { cc_doc: ct },
    }, { headers })
    const { url, key } = res.data.files.cc_doc
    await axios.put(url, file, { headers: { 'Content-Type': ct } })
    return { key, nombre: file.name }
}

function OPFormModal({ empresaId, op, onSaved, onClose, setMsg, canChangeStatus }) {
    const editing = !!op

    const [form, setForm] = useState({
        op_numero:       '',
        numero_factura:  '',
        proveedor:       '',
        valor_factura:   '',
        moneda:          'USD',
        estado:          'activa',
    })
    const [file,     setFile]     = useState(null)
    const [saving,   setSaving]   = useState(false)
    const [uploading, setUploading] = useState(false)

    useEffect(() => {
        if (op) {
            setForm({
                op_numero:      op.op_numero      || '',
                numero_factura: op.numero_factura || '',
                proveedor:      op.proveedor      || '',
                valor_factura:  op.valor_factura  != null ? String(op.valor_factura) : '',
                moneda:         op.moneda         || 'USD',
                estado:         op.estado         || 'activa',
            })
        }
    }, [op])

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!form.op_numero.trim()) { setMsg({ type: 'error', text: 'El campo OP es obligatorio' }); return }

        setSaving(true)
        try {
            let factura_s3_key = op?.factura_s3_key || ''
            let factura_nombre = op?.factura_nombre || ''

            if (file) {
                setUploading(true)
                const uploaded = await uploadDocToS3(file, empresaId)
                factura_s3_key = uploaded.key
                factura_nombre = uploaded.nombre
                setUploading(false)
            }

            const headers = await authHeaders()
            const payload = {
                ...form,
                valor_factura: parseFloat(form.valor_factura) || 0,
                factura_s3_key,
                factura_nombre,
            }
            if (!canChangeStatus) delete payload.estado

            if (editing) {
                await axios.put(`${API}/cuenta-compensacion/${empresaId}/ops/${op.op_id}`, payload, { headers })
            } else {
                await axios.post(`${API}/cuenta-compensacion/${empresaId}/ops`, payload, { headers })
            }
            onSaved()
        } catch (err) {
            setMsg({ type: 'error', text: 'Error guardando OP: ' + (err.response?.data?.error || err.message) })
        } finally {
            setSaving(false)
            setUploading(false)
        }
    }

    return (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>{editing ? 'Editar Orden de Pedido' : 'Nueva Orden de Pedido'}</h2>
                    <button className={styles.btnClose} onClick={onClose}>✕</button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {/* OP Número — campo principal */}
                    <div className={styles.fieldFull}>
                        <label>OP (Orden de Pedido) <span className={styles.required}>*</span></label>
                        <input
                            value={form.op_numero}
                            onChange={e => set('op_numero', e.target.value)}
                            placeholder="Ej: P00838"
                            required
                            autoFocus
                        />
                        <p className={styles.hint}>Identificador único de la orden de pedido</p>
                    </div>

                    {/* Proveedor */}
                    <div className={styles.fieldFull}>
                        <label>Proveedor</label>
                        <input
                            value={form.proveedor}
                            onChange={e => set('proveedor', e.target.value)}
                            placeholder="Nombre del proveedor en el exterior"
                        />
                    </div>

                    {/* Número Factura */}
                    <div className={styles.field}>
                        <label>Número de Factura</label>
                        <input
                            value={form.numero_factura}
                            onChange={e => set('numero_factura', e.target.value)}
                            placeholder="Ej: INV-1212641"
                        />
                    </div>

                    {/* Valor + Moneda */}
                    <div className={styles.fieldGroup}>
                        <div className={styles.field}>
                            <label>Valor de Factura</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.valor_factura}
                                onChange={e => set('valor_factura', e.target.value)}
                                placeholder="0.00"
                            />
                        </div>
                        <div className={styles.fieldSmall}>
                            <label>Moneda</label>
                            <select value={form.moneda} onChange={e => set('moneda', e.target.value)}>
                                {MONEDAS.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Factura comercio exterior (adjunto) */}
                    <div className={styles.fieldFull}>
                        <label>Factura Comercio Exterior (adjunto)</label>
                        <div className={styles.fileArea}>
                            <input
                                type="file"
                                id="file-factura"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                onChange={e => setFile(e.target.files[0] || null)}
                                className={styles.fileInput}
                            />
                            <label htmlFor="file-factura" className={styles.fileLabel}>
                                {file ? (
                                    <span className={styles.fileSelected}>📎 {file.name}</span>
                                ) : op?.factura_nombre ? (
                                    <span className={styles.fileExisting}>📄 {op.factura_nombre} <em>(reemplazar)</em></span>
                                ) : (
                                    <span>📎 Seleccionar PDF, imagen o documento…</span>
                                )}
                            </label>
                        </div>
                        <p className={styles.hint}>Formatos: PDF, JPG, PNG, DOC, DOCX, XLSX (máx. 10 MB)</p>
                    </div>

                    {/* Estado (solo al editar) */}
                    {editing && canChangeStatus && (
                        <div className={styles.field}>
                            <label>Estado</label>
                            <select value={form.estado} onChange={e => set('estado', e.target.value)}>
                                <option value="activa">Activa</option>
                                <option value="cerrada">Cerrada</option>
                            </select>
                        </div>
                    )}

                    <div className={styles.actions}>
                        <button type="button" className={styles.btnCancel} onClick={onClose}>Cancelar</button>
                        <button type="submit" className={styles.btnSave} disabled={saving}>
                            {uploading ? '⬆ Subiendo archivo…' : saving ? 'Guardando…' : editing ? 'Actualizar OP' : 'Crear OP'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default OPFormModal
